import {
  CalendarDays,
  Clock,
  FileImage,
  FileSearch,
  FileText,
  Hammer,
  Home,
  Package,
  Receipt,
  RefreshCw,
  ScanLine,
  ShieldAlert,
  Sparkles,
  StickyNote,
  Target,
  UserPlus,
} from 'lucide-react-native';
import type { ComponentType } from 'react';

/**
 * Canonical list of every action available in the My Home quick toolbar.
 * The user picks any subset, in any order, via `/quick-toolbar`. Action
 * keys are stable strings — store them in `property.quick_toolbar_keys_json`
 * so we never persist component or icon references.
 *
 * Action types:
 *   - Navigation tools (default-active): jump to a tab/list/page
 *   - Document exports (default-inactive, opt-in): generate a PDF / open the
 *     emergency card
 *   - Tune-like verbs (default-inactive, opt-in): tune plan, replan year
 *
 * Removed in this version: `add-system` and `custom-task` — both already
 * have permanent surfaces (the global "Add a system" button on My Home,
 * and the Quick Actions menu off the header) so they were redundant in
 * the toolbar.
 */

export interface QuickAction {
  key: string;
  label: string;
  /** Where this action navigates to. */
  route: string;
  /** Primary actions get the filled forest treatment. Pure styling hint. */
  primary?: boolean;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
}

export const QUICK_ACTIONS: QuickAction[] = [
  // Smart Scan leads the row (Wave 54, user call) — forest primary
  // because it's THE signature action: any paper in, filed
  // automatically. Edit Home was removed outright: the pencil on the
  // home card above the toolbar and the Homes manager both already
  // open the editor, so a third entry was noise.
  { key: 'scan',        label: 'AI Scan',   route: '/scan',           Icon: ScanLine,       primary: true },
  { key: 'schedule',    label: 'Schedule',     route: '/(tabs)/schedule', Icon: CalendarDays },
  { key: 'systems',     label: 'Systems',      route: '/systems',        Icon: Home },
  { key: 'inventory',   label: 'Inventory',    route: '/inventory',      Icon: Package },
  { key: 'expenses',    label: 'Expenses',     route: '/expenses',       Icon: Receipt },
  { key: 'quotes',      label: 'Quotes',       route: '/quotes',         Icon: FileSearch },
  { key: 'goals',       label: 'Projects',     route: '/projects',       Icon: Target },
  { key: 'media',       label: 'Files',        route: '/media',          Icon: FileImage },
  { key: 'contacts',    label: 'Contacts',     route: '/contacts',       Icon: UserPlus },
  { key: 'notes',       label: 'Notes',        route: '/notes',          Icon: StickyNote },
  { key: 'timeline',    label: 'Timeline',     route: '/timeline',       Icon: Clock },
  { key: 'find-pro',    label: 'Find Pro',     route: '/pros',           Icon: Hammer },
  // Opt-in extras below this line — not in DEFAULT_TOOLBAR_KEYS but
  // available in the Manage Toolbar editor. These are export/verb-style
  // actions that not everyone wants on their always-visible toolbar.
  { key: 'home-pdf',    label: 'Home Record File', route: '/(tabs)/my-home', Icon: FileText },
  { key: 'emergency',   label: 'Emergency Card',   route: '/emergency-card', Icon: ShieldAlert },
  { key: 'tune',        label: 'Tune plan',    route: '/profile-setup',  Icon: Sparkles },
  { key: 'replan',      label: 'Replan',       route: '/(tabs)/my-home', Icon: RefreshCw },
];

/** Default-active toolbar: 10 navigation tools, in the order the user
 *  asked for. The four opt-in extras (home-pdf, emergency, tune, replan)
 *  are NOT included here — the user enables them via the Manage Toolbar
 *  editor when they want them on the strip. */
export const DEFAULT_TOOLBAR_KEYS: string[] = [
  // Wave 54 order (user-specified): Smart Scan FIRST in the forest
  // primary style; Edit Home removed (redundant with the home card's
  // edit affordance and the Homes manager).
  'scan',
  'schedule',
  'systems',
  'quotes',
  'expenses',
  'goals',
  'inventory',
  'media',
  'contacts',
  'notes',
  'timeline',
  'find-pro',
];

/**
 * Keys we ALWAYS want on the toolbar, regardless of what the user
 * saved earlier. Right now this is just `edit-home` because we added
 * it as the new primary action after some users already had a saved
 * list — without this auto-injection their saved list would never
 * surface the new primary tile. If they intentionally remove it via
 * the Manage Toolbar editor, the editor writes the result back via
 * the same `parseToolbarKeys` path so the injection kicks in again
 * on the next read (which is fine — Edit Home is too important to
 * accidentally hide).
 *
 * If we ever ship another "must show" tile, add its key here.
 */
/**
 * Keys we always make sure show up on the toolbar, with an optional
 * `after` hint that controls placement when injecting into an existing
 * saved list. If `after` is missing or its anchor isn't present we fall
 * back to the front of the row.
 */
interface MustInject {
  key: string;
  after?: string;
}
const MUST_INJECT_KEYS: MustInject[] = [
  // Smart Scan (Wave 54) — the primary action, pinned to the front of
  // every saved list. (The scan-after-systems inject below becomes a
  // no-op once this one runs, kept for ordering docs.)
  { key: 'scan' },
  // Notes (Wave 35) — slot it between Contacts and Timeline to match
  // the new default ordering. Users who had `contacts` saved will see
  // Notes appear immediately after it; users without `contacts` get
  // Notes at the front, which is also fine (still visible, still
  // one tap away).
  { key: 'notes', after: 'contacts' },
  // Quotes (Wave 45) — surfaces the new tool for users with a saved
  // pre-45 toolbar. Anchored after Expenses so the money pair stays
  // adjacent.
  { key: 'quotes', after: 'expenses' },
  // Smart Scan (Wave 48) — the auto-filing scanner deserves a spot on
  // every saved toolbar; anchored after Systems per the default order.
  { key: 'scan', after: 'systems' },
];

export function parseToolbarKeys(raw: string | null | undefined): string[] {
  if (!raw) return DEFAULT_TOOLBAR_KEYS;
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return DEFAULT_TOOLBAR_KEYS;
    const allKeys = new Set(QUICK_ACTIONS.map((a) => a.key));
    const cleaned = (arr as unknown[])
      .filter((k): k is string => typeof k === 'string')
      // Dropping a key from QUICK_ACTIONS retires it from saved lists
      // too (Wave 54: edit-home) — allKeys is the source of truth.
      .filter((k) => allKeys.has(k));
    if (cleaned.length === 0) return DEFAULT_TOOLBAR_KEYS;
    // Auto-inject the must-show keys. Each gets a placement hint:
    // either right after a named anchor (e.g. notes-after-contacts)
    // or at the front of the row if the anchor isn't in the user's
    // list. Preserves the user's order for everything else.
    const final = cleaned.slice();
    for (const inject of MUST_INJECT_KEYS) {
      if (final.includes(inject.key)) continue;
      const anchorIdx = inject.after ? final.indexOf(inject.after) : -1;
      if (anchorIdx >= 0) {
        final.splice(anchorIdx + 1, 0, inject.key);
      } else {
        final.unshift(inject.key);
      }
    }
    return final;
  } catch {
    return DEFAULT_TOOLBAR_KEYS;
  }
}

export function getActionByKey(key: string): QuickAction | null {
  return QUICK_ACTIONS.find((a) => a.key === key) ?? null;
}
